import Button from "~/components/ui/Button"
import { invoke } from "~/runtime"
import { useSignal } from "~/sdk/signal"
import type React from "react"
import { Ref } from "react"
import { useRef } from "react"

interface InputProps {
  id: string
  placeholder: string
  label: string
  inputRef: Ref<HTMLInputElement>
  error: string
  maxLength?: number
}

interface BrandContactFormProps {
  brand: string
  submit: string
  message: string
}

function Input({ id, placeholder, label, inputRef, error, maxLength }: InputProps) {
  return (
    <div className='w-full flex flex-col gap-[6px]'>
      <div className='flex w-full justify-between items-center'>
        <label
          htmlFor={id}
          className='text-[#361328] text-sm font-medium'
        >
          {label}
        </label>
        <span className='text-[#91153A] text-xs'>{error}</span>
      </div>

      <input
        type='text'
        maxLength={maxLength ?? 100}
        id={id}
        ref={inputRef}
        className='h-11 w-full text-sm text-[#494949] placeholder:text-[#494949] bg-white border border-white shadow-md shadow-[#00000019] px-4 duration-300 hover:border-[#CACACA] rounded-lg outline-none focus:border-[#CACACA]'
        placeholder={placeholder}
      />
    </div>
  )
}

function BrandContactForm({ brand, submit, message }: BrandContactFormProps) {
  const nameInputRef = useRef<HTMLInputElement>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const phoneInputRef = useRef<HTMLInputElement>(null)

  const errors = useSignal({ name: '', email: '', phone: '' })
  const feedbackMessage = useSignal('')

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const name = nameInputRef.current?.value
    const email = emailInputRef.current?.value
    const phone = phoneInputRef.current?.value

    errors.value = { name: '', email: '', phone: '' }

    if (!name) return errors.value.name = 'Insira um nome'
    if (name?.length <= 2) return errors.value.name = 'Insira um nome válido'
    if (!email) return errors.value.email = 'Insira um e-mail'
    if (!isEmailValid()) return errors.value.email = 'Insira um e-mail válido'
    if (!phone) return errors.value.phone = 'Insira um WhatsApp'
    if (phone?.length <= 10 || phone?.length >= 17) {
      return errors.value.phone = 'Insira um WhatsApp válido'
    }

    const data = { name, email, phone, brand: brand }

    await invoke.vtex.actions.masterdata.createDocument({
      acronym: 'BC',
      data,
    }).then(() => feedbackMessage.value = 'success').catch((error) => {
      console.error('ERROR', error)
      feedbackMessage.value = 'error'
    })
  }

  function isEmailValid() {
    const email = emailInputRef.current?.value
    return email?.match(/^[^@]+@[^@]+\.[^@]+$/)
  }

  return (
    <form
      onSubmit={onSubmit}
      className='w-full lg:w-[420px] xl:w-[590px] flex flex-col bg-[#EADFF1] rounded-xl py-5 px-5 xl:px-10 gap-2 my-auto'
    >
      {feedbackMessage.value === 'success' && (
        <>
          <span className='text-lg font-bold text-[#361328] text-center'>
            Recebemos a sua inscrição!
          </span>
          <span className='text-[#595959] text-center text-base'>{message}</span>
        </>
      )}

      {feedbackMessage.value === 'error' && (
        <>
          <span className='text-lg font-bold text-[#595959] text-center'>Mensagem não enviada...</span>
          <span className='text-[#595959] text-center text-base'>Tente novamente mais tarde!</span>
        </>
      )}

      {!feedbackMessage.value && (
        <>
          <Input
            id='name'
            label='Nome'
            placeholder='insira seu nome'
            inputRef={nameInputRef}
            error={errors.value.name}
          />

          <Input
            id='email'
            label='E-mail'
            placeholder='insira seu e-mail'
            inputRef={emailInputRef}
            error={errors.value.email}
          />

          <Input
            id='phone'
            label='Whatsapp'
            placeholder='(00) 0 0000-0000'
            inputRef={phoneInputRef}
            error={errors.value.phone}
            maxLength={16}
          />

          <Button
            type='submit'
            className='w-full h-11 bg-[#E5554F] border-[#E5554F] rounded-lg text-white uppercase text-sm font-bold duration-300 hover:bg-[#BF241E] hover:border-[#BF241E]'
          >
            {submit}
          </Button>
        </>
      )}
    </form>
  )
}

export default BrandContactForm
